import React, { useMemo } from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { AnimateLayoutChanges } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

import type { RawSortableItemProps } from './RawSortableItem';
import { RawSortableItem } from './RawSortableItem';
import { SortableEmptyState } from './SortableEmptyState';
import { useSortableContext } from './SortableContext';

const animateLayoutChanges: AnimateLayoutChanges = args =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

const emptyStateComponent = <SortableEmptyState />;

export type RawSortableValueType = string[];

export type RawSortableListProps = {
  dismissible: RawSortableItemProps['dismissible'];
  dismissibleIcon: RawSortableItemProps['dismissibleIcon'];
  emptyComponent?: React.ComponentType | React.ReactElement;
  id?: string;
  name: string;
  onAfterRemove: (
    removedItem: UniqueIdentifier,
    updatedList: RawSortableValueType
  ) => void;
  hideHandle: RawSortableItemProps['hideHandle'];
  showDropPlaceholder: boolean;
  value: RawSortableValueType;
};

export const RawSortableList = ({
  dismissible,
  dismissibleIcon,
  emptyComponent = emptyStateComponent,
  id,
  name,
  onAfterRemove,
  hideHandle,
  showDropPlaceholder,
  value,
}: RawSortableListProps) => {
  const { isDragging, setNodeRef, transition, transform } = useSortable({
    id: name,
    data: {
      children: value,
      containerId: name,
      type: 'container',
    },
    animateLayoutChanges,
  });

  const {
    displayByValueLookup,
    dataByValueLookup,
    shouldHighlight,
    onItemClicked,
  } = useSortableContext();

  const items = value;

  const handleRemove = (itemId: UniqueIdentifier) => {
    const newItems = items.filter(item => item !== itemId);
    onAfterRemove(itemId, newItems);
  };

  const emptyContent = useMemo(
    () =>
      React.isValidElement(emptyComponent)
        ? emptyComponent
        : React.createElement(emptyComponent as React.ComponentType),
    [emptyComponent]
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
      }}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
        id={name}
      >
        <div
          className={AttrsHelper.formatClassname(
            'sortable',
            value.length === 0 && 'sortable-empty',
            showDropPlaceholder && 'show-drop-placeholder'
          )}
          data-test={name}
          data-value={JSON.stringify(value)}
          id={id}
        >
          {items.length === 0
            ? emptyContent
            : items.map(currentValue => {
                const itemData = dataByValueLookup[currentValue];
                const isHighlighted =
                  itemData && shouldHighlight?.(itemData)
                    ? shouldHighlight(itemData)
                    : false;
                return (
                  <RawSortableItem
                    key={currentValue}
                    value={currentValue}
                    dismissible={dismissible}
                    dismissibleIcon={dismissibleIcon}
                    onRemove={handleRemove}
                    hideHandle={hideHandle}
                    name={name}
                    isHighlighted={isHighlighted}
                    onItemClicked={
                      onItemClicked && itemData
                        ? () => {
                            onItemClicked(itemData);
                          }
                        : undefined
                    }
                  >
                    {displayByValueLookup[currentValue]}
                  </RawSortableItem>
                );
              })}
        </div>
      </SortableContext>
    </div>
  );
};
